const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
class CoastalFilm extends HTMLElement {
  connectedCallback() {
    if (this.video) return;
    this.video = this.querySelector('video');
    this.button = this.querySelector('button');
    this.status = this.querySelector('[role="status"]');
    this.labels = this.querySelector('[data-film-labels]')?.dataset;
    if (!this.video || !this.button || !this.status || !this.labels) { this.video = null; return; }
    this.loaded = false;
    this.failed = false;
    this.status.textContent = '';
    this.removeAttribute('data-ready');
    this.video.muted = true;
    this.button.hidden = false;
    this.userPaused = false;
    this.inView = false;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.button.addEventListener('click', () => {
      if (!this.video.paused) { this.userPaused = true; this.video.pause(); }
      else { this.userPaused = false; this.playFilm(); }
    }, options);
    this.video.addEventListener('playing', () => { this.setAttribute('data-ready', ''); this.button.textContent = this.labels.pause; }, options);
    this.video.addEventListener('pause', () => { this.button.textContent = this.labels.play; }, options);
    this.video.addEventListener('error', () => this.fail(), options);
    this.preferenceChanged = () => { if (reducedMotion.matches) this.video.pause(); else this.autoPlay(); };
    reducedMotion.addEventListener('change', this.preferenceChanged, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); else this.autoPlay(); }, options);
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries.some(entry => entry.isIntersecting);
      if (!this.inView) this.video.pause(); else this.autoPlay();
    }, { threshold: 0.15 });
    this.observer.observe(this);
  }
  loadFilm() {
    if (this.loaded) return;
    this.loaded = true;
    this.video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
    this.video.load();
  }
  async playFilm() {
    if (this.failed) return;
    this.loadFilm();
    try { await this.video.play(); }
    catch (error) {
      if (error.name === 'AbortError' || error.name === 'NotAllowedError') this.button.textContent = this.labels.play;
      else this.fail();
    }
  }
  autoPlay() {
    if (this.inView && !document.hidden && !this.userPaused && !reducedMotion.matches && !navigator.connection?.saveData) this.playFilm();
  }
  fail() {
    this.failed = true;
    this.video.pause();
    this.removeAttribute('data-ready');
    this.button.hidden = true;
    this.status.textContent = this.labels.error;
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
    this.video?.pause();
    this.video = null;
  }
}
if (!customElements.get('sc-coastal-film')) customElements.define('sc-coastal-film', CoastalFilm);
class CoastalCarousel extends HTMLElement {
  connectedCallback() {
    if (this.abort || !this.classList.contains('sc-notes--carousel')) return;
    this.track = this.querySelector('.sc-notes__grid');
    this.controls = this.querySelector('.sc-notes__controls');
    if (!this.track || !this.controls) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.buttons = [...this.controls.querySelectorAll('button[data-direction]')];
    this.controls.hidden = false;
    this.buttons.forEach(button => button.addEventListener('click', () => {
      const direction = Number(button.dataset.direction);
      if (direction !== 1 && direction !== -1) return;
      this.track.scrollBy({ left: direction * (this.track.querySelector('article')?.getBoundingClientRect().width + 12 || this.track.clientWidth), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }, options));
    this.update = () => {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.buttons.forEach(button => { button.disabled = Number(button.dataset.direction) < 0 ? this.track.scrollLeft <= 1 : this.track.scrollLeft >= maximum - 1; });
    };
    this.track.addEventListener('scroll', this.update, options);
    this.resize = new ResizeObserver(this.update);
    this.resize.observe(this.track);
    this.update();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.resize?.disconnect();
    this.abort = null;
  }
}
if (!customElements.get('sc-coastal-carousel')) customElements.define('sc-coastal-carousel', CoastalCarousel);
const revealed = new WeakSet();
const revealObserver = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    revealObserver.unobserve(entry.target);
    if (!reducedMotion.matches && entry.target.animate) entry.target.animate([{opacity: 0.7, transform: 'translateY(12px)'}, {opacity: 1, transform: 'translateY(0)'}], {duration: 550, easing: 'ease-out'});
  });
}, { threshold: 0.12 });
function observeStories() {
  document.querySelectorAll('.sc-story:not(.sc-story--hero) .sc-story__copy, .sc-notes__card, .sc-looks__look, .sc-collections__card').forEach(element => {
    if (revealed.has(element)) return;
    revealed.add(element);
    revealObserver.observe(element);
  });
}
observeStories();
document.addEventListener('shopify:section:load', observeStories);
